import React, { useState, useEffect, useMemo } from 'react';
import { securePost } from '../../config/api';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { SaveCancelTop } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import { formatAmount } from '../../utils/format';

import plusIcon from '../../icons/plus.svg';
import deleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import noImage from '../../icons/noImageIcon.svg';

const DeliveryNoteForm = ({
  title,
  baseFields = [],
  listFields = [],
  products = [],
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const [formValues, setFormValues] = useState({});
  const [lineList, setLineList] = useState([]);
  const [currentLine, setCurrentLine] = useState({ productId: '', quantity: '' });
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const initialValues = {};
    baseFields.forEach((field) => {
      initialValues[field.name] = field.value ?? '';
    });
    setFormValues(initialValues);
  }, [baseFields]);

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleListFieldChange = (name, value) => {
    setCurrentLine((prev) => ({ ...prev, [name]: value }));
  };

  const getLineTotalHT = (line) => {
    const unitPrice = Number(line.unitPrice) || 0;
    const qty = Number(line.quantity) || 0;
    const discount = Number(line.discount) || 0;
    return qty * unitPrice * (1 - discount / 100);
  };

  const getLineTotalTTC = (line) => {
    const totalHT = getLineTotalHT(line);
    const tva = Number(line.tva) || 0;
    return totalHT * (1 + tva / 100);
  };

  const addLine = () => {
    const { productId, quantity } = currentLine;
    if (!productId || !quantity || Number(quantity) <= 0) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    const product = products.find((p) => p.value === Number(productId) || p.value === productId);
    if (!product) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    const full = product.fullEntity || {};
    const unitPrice = full.priceHT != null ? Number(full.priceHT) : 0;
    const tva = full.tva != null ? Number(full.tva) : (full.vatRate != null ? Number(full.vatRate) : 20);
    const discount = full.discount != null ? Number(full.discount) : 0;
    setLineList((prev) => [
      ...prev,
      {
        productId: product.value,
        productName: product.name,
        reference: product.reference,
        quantity: parseInt(quantity, 10),
        image: product.fullEntity?.image,
        unitPrice,
        tva,
        discount,
      },
    ]);
    setCurrentLine({ productId: '', quantity: '' });
  };

  const removeLine = (index) => {
    setLineList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, delta) => {
    setLineList((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
      )
    );
  };

  const { totalHT, totalTTC } = useMemo(() => {
    let ht = 0;
    let ttc = 0;
    lineList.forEach((line) => {
      ht += getLineTotalHT(line);
      ttc += getLineTotalTTC(line);
    });
    return { totalHT: ht, totalTTC: ttc };
  }, [lineList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingBase = baseFields.filter((f) => f.required && !formValues[f.name]);
    if (missingBase.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missingBase.map((f) => f.label).join(', ')}`, 'error');
      return;
    }
    if (lineList.length === 0) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }

    const payload = {
      cartId: parseInt(formValues.cartId, 10),
      repoId: parseInt(formValues.repoId, 10),
      details: lineList.map((line) => ({
        productId: line.productId,
        quantity: line.quantity,
      })),
    };

    try {
      const response = await securePost('/deliveryNotes/add', payload, showSnackbar);
      if (response?.status === 200 || response?.status === 201) {
        onSuccess?.();
        onSubmit?.(response?.data);
      }
    } catch (error) {
      console.error('Delivery note submit error:', error);
    }
  };


  return (
    <div className="w-full">
      <Helmet>
        <title>Drivnest - {t('titles.bl')}</title>
      </Helmet>

      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={onCancel} onSave={handleSubmit} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mt-4">
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1">
            {baseFields.map((field, index) => (
              <Input
                key={field.name || index}
                type={field.type || 'text'}
                label={field.label}
                name={field.name}
                value={formValues[field.name] ?? ''}
                required={field.required}
                onChange={(value) => handleInputChange(field.name, value)}
                options={field.options || []}
              />
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t('fields_types.products')}</h3>
          <div className="flex items-end gap-2 flex-wrap">
            {listFields.map((field, index) => (
              <div key={field.name || index} className="flex-1 min-w-[120px]">
                <Input
                  type={field.type || 'text'}
                  label={field.label}
                  name={field.name}
                  required={field.required}
                  value={currentLine[field.name] ?? ''}
                  onChange={(value) => handleListFieldChange(field.name, value)}
                  options={
                    field.name === 'productId'
                      ? (products || []).map((p) => ({ value: p.value, label: p.name || p.label, ...p }))
                      : field.options || []
                  }
                />
              </div>
            ))}
            <button
              type="button"
              onClick={addLine}
              className="bg-[#16283E] text-white w-[48px] h-[48px] rounded-[12px] hover:bg-[#3F3456] flex items-center justify-center flex-shrink-0"
            >
              <img src={plusIcon} alt="Add" className="w-4 h-4" />
            </button>
          </div>

        </div>

        {lineList.length > 0 && (
          <div className="mt-4">
            <div className="grid grid-cols-[18%_10%_8%_8%_10%_12%_0.5fr] items-center text-[#919191] text-sm font-semibold mb-2 px-6">
              <div className="text-left">{t('rows.product')}</div>
              <div className="text-center">{t('rows.priceuHT')}</div>
              <div className="text-center">TVA (%)</div>
              <div className="text-center">{t('rows.discount')}</div>
              <div className="text-center">{t('rows.quantity')}</div>
              <div className="text-center">Total TTC</div>
              <div className="text-right"></div>
            </div>
            {lineList.map((line, index) => (
              <div
                key={index}
                className="grid grid-cols-[18%_10%_8%_8%_10%_12%_0.5fr] items-center py-4 px-6 rounded-[12px] bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] mb-2"
              >
                <div className="flex items-center gap-4 text-left">
                  <div>
                    <p className="text-[#043F88] text-[12px] font-semibold">{line.reference}</p>
                    <p className="text-[#6B78F8] text-[16px] font-semibold">{line.productName}</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-[#043F88] text-[14px] font-semibold">{formatAmount(line.unitPrice)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#043F88] text-[14px] font-semibold">{line.tva}%</p>
                </div>
                <div className="text-center">
                  <p className="text-[#043F88] text-[14px] font-semibold">{line.discount}%</p>
                </div>
                <div className="flex justify-center">
                  <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6C78F8]/20 divide-x divide-[#6C78F8]">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(index, -1)}
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={minus} alt="Minus" className="w-4 h-4" />
                    </button>
                    <span className="min-w-[60px] px-2 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                      {line.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(index, 1)}
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                    >
                      <img src={plus} alt="Plus" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-[#043F88] text-[14px] font-semibold">{formatAmount(getLineTotalTTC(line))}</p>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center bg-[#F2F3F3] rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] cursor-pointer hover:bg-red-50"
                    onClick={() => removeLine(index)}
                  >
                    <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-end gap-8 mt-4 px-6 py-3 rounded-[12px] bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
              <div className="flex items-center gap-2">
                <span className="text-[#38485C] font-semibold">Total HT :</span>
                <span className="text-[#043F88] text-[18px] font-semibold">{formatAmount(totalHT)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#38485C] font-semibold">Total TTC :</span>
                <span className="text-[#043F88] text-[18px] font-semibold">{formatAmount(totalTTC)}</span>
              </div>
            </div>
          </div>
        )}
      </form>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default DeliveryNoteForm;
